import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { receiveApi } from '../lib/api';
import { copyToClipboard } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Copy, QrCode, Building, Smartphone, Mail, User } from 'lucide-react';

const ReceiveMoneyPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [details, setDetails] = useState(null);

  useEffect(() => { fetchDetails(); }, []);

  const fetchDetails = async () => {
    try {
      const res = await receiveApi.getDetails();
      setDetails(res.data);
    } catch (error) {
      toast.error('Failed to load receive details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async (text, label) => {
    await copyToClipboard(text);
    toast.success(`${label} copied`);
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6 lg:p-0 space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-8">
        {/* Header */}
        <div className="p-6 lg:p-0 lg:mb-6 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/5 rounded-lg transition-colors lg:hidden"><ArrowLeft className="w-6 h-6" /></button>
          <h1 className="text-xl lg:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Receive Money</h1>
        </div>
        
        <div className="px-6 lg:px-0 lg:grid lg:grid-cols-2 lg:gap-6 space-y-6 lg:space-y-0">
          {/* QR Code / LekeeWallet ID */}
          <div className="bg-card border border-white/10 rounded-2xl p-6 text-center">
            <div className="w-48 h-48 mx-auto bg-white rounded-2xl mb-6 flex items-center justify-center">
              <QrCode className="w-24 h-24 text-gray-800" />
            </div>
            <p className="text-muted-foreground mb-2">Your LekeeWallet ID</p>
            <div className="flex items-center justify-center gap-2">
              <p className="text-xl font-bold font-mono" data-testid="lekeewallet-id">{details?.lekeewallet_id}</p>
              <button onClick={() => handleCopy(details?.lekeewallet_id, 'LekeeWallet ID')} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <Copy className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Bank Transfer Details */}
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center"><Building className="w-5 h-5 text-blue-500" /></div>
                <div><p className="font-medium">Bank Transfer</p><p className="text-sm text-muted-foreground">Fund via bank</p></div>
              </div>
              <div className="bg-white/5 rounded-xl p-4 space-y-3">
                <InfoRow label="Bank" value={details?.virtual_account?.bank} />
                <InfoRow label="Account Number" value={details?.virtual_account?.account_number} copiable onCopy={() => handleCopy(details?.virtual_account?.account_number, 'Account number')} />
                <InfoRow label="Account Name" value={details?.virtual_account?.account_name} />
              </div>
            </div>

            {/* Other Ways */}
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <p className="font-medium mb-4">Other receive methods</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                  <Smartphone className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1"><p className="text-sm font-medium">Phone Number</p><p className="text-xs text-muted-foreground">{details?.phone}</p></div>
                  <button onClick={() => handleCopy(details?.phone, 'Phone number')} className="p-1 hover:bg-white/10 rounded"><Copy className="w-4 h-4 text-muted-foreground" /></button>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1"><p className="text-sm font-medium">Email</p><p className="text-xs text-muted-foreground">{details?.email}</p></div>
                  <button onClick={() => handleCopy(details?.email, 'Email')} className="p-1 hover:bg-white/10 rounded"><Copy className="w-4 h-4 text-muted-foreground" /></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

const InfoRow = ({ label, value, copiable, onCopy }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-muted-foreground">{label}</span>
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium">{value}</span>
      {copiable && <button onClick={onCopy} className="p-1 hover:bg-white/10 rounded"><Copy className="w-4 h-4 text-muted-foreground" /></button>}
    </div>
  </div>
);

export default ReceiveMoneyPage;
